/**
 * AlertsPanel - Phase 3.1 Dashboard Enhancement
 * 
 * Shows real-time alerts with filtering by severity.
 * Supports WebSocket updates for live notifications.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  X,
  Filter,
  ChevronDown,
  ExternalLink,
  Clock,
  Loader2,
  RefreshCw
} from 'lucide-react';

type AlertSeverity = 'critical' | 'warning' | 'info' | 'success';

interface Alert {
  id: string;
  title: string;
  message: string;
  severity: AlertSeverity;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  source?: string;
  reportId?: string;
  draftId?: string;
}

interface AlertsPanelProps {
  maxAlerts?: number;
  className?: string;
  showFilter?: boolean;
  compact?: boolean;
}

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconColor: 'text-red-600',
    textColor: 'text-red-800',
    badge: 'bg-red-100 text-red-700'
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    iconColor: 'text-orange-600',
    textColor: 'text-orange-800',
    badge: 'bg-orange-100 text-orange-700'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconColor: 'text-blue-600',
    textColor: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-700'
  },
  success: {
    icon: CheckCircle,
    bg: 'bg-green-50',
    border: 'border-green-200',
    iconColor: 'text-green-600',
    textColor: 'text-green-800',
    badge: 'bg-green-100 text-green-700'
  }
};

export default function AlertsPanel({ 
  maxAlerts = 10, 
  className = '',
  showFilter = true,
  compact = false
}: AlertsPanelProps) {
  const { getToken } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<AlertSeverity | 'all'>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Set up polling for updates (will be replaced with WebSocket in Phase 5)
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/dashboard/alerts', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts((data.alerts || []).slice(0, maxAlerts));
    } catch (err) {
      console.debug('Alerts API not available, using fallback');
      // Fallback to empty - no mock alerts needed
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      // Optimistically remove from UI
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }
  };

  const markAsRead = async (alertId: string) => {
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
    } catch (err) {
      // Optimistically mark as read
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.severity === filter);

  const unreadCount = alerts.filter(a => !a.read).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center">
          <Bell className="w-5 h-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
          {unreadCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
              {unreadCount} new
            </span>
          )}
          {criticalCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
              {criticalCount} critical
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {showFilter && (
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4 mr-1" />
                {filter === 'all' ? 'All' : filter}
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  {(['all', 'critical', 'warning', 'info', 'success'] as const).map(option => (
                    <button
                      key={option}
                      onClick={() => {
                        setFilter(option);
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        filter === option ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {option === 'all' ? 'All Alerts' : option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={fetchAlerts}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh alerts"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Alert list */}
      <div className={`divide-y divide-gray-100 ${compact ? 'max-h-64' : 'max-h-96'} overflow-y-auto`}>
        {filteredAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
            <p className="text-gray-600 font-medium">No alerts</p>
            <p className="text-gray-500 text-sm">You're all caught up!</p>
          </div>
        ) : (
          filteredAlerts.map(alert => {
            const config = SEVERITY_CONFIG[alert.severity];
            const Icon = config.icon;

            return (
              <div
                key={alert.id}
                className={`p-4 ${!alert.read ? config.bg : 'bg-white'} hover:bg-gray-50 transition-colors`}
                onClick={() => !alert.read && markAsRead(alert.id)}
              >
                <div className="flex items-start">
                  <div className={`p-1.5 rounded-lg ${config.bg} mr-3 flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${config.iconColor}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className={`text-sm font-medium ${!alert.read ? 'text-gray-900' : 'text-gray-700'}`}>
                        {alert.title}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissAlert(alert.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTime(alert.timestamp)}
                        {alert.source && (
                          <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded">
                            {alert.source}
                          </span>
                        )}
                      </div>
                      
                      {alert.actionUrl && (
                        <a
                          href={alert.actionUrl}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-700 font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {alert.actionLabel || 'View'}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
